import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "vv_admin_token";
const USER_KEY = "vv_admin_user";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getUsername = () => localStorage.getItem(USER_KEY);
export const setSession = (token, username) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, username);
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const api = axios.create({ baseURL: API });

// Attach token if present
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-clear session on 401 so protected pages can redirect to login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearSession();
      // Broadcast so React contexts can react
      window.dispatchEvent(new CustomEvent("vv:auth-expired"));
    }
    return Promise.reject(err);
  }
);

// ---------- Auth ----------
export const login = (username, password) =>
  api.post("/auth/login", { username, password }).then((r) => r.data);

export const me = () => api.get("/auth/me").then((r) => r.data);

// ---------- Applications ----------
export const createApplication = (data) =>
  api.post("/applications", data).then((r) => r.data);

export const listApplications = (status) =>
  api.get("/applications", { params: status ? { status } : {} }).then((r) => r.data);

export const getApplication = (id) =>
  api.get(`/applications/${id}`).then((r) => r.data);

export const updateApplicationStatus = (id, status) =>
  api.patch(`/applications/${id}`, { status }).then((r) => r.data);

export const deleteApplication = (id) =>
  api.delete(`/applications/${id}`).then((r) => r.data);

export const getStats = () =>
  api.get("/applications/stats").then((r) => r.data);

// ---------- Free shoot / mood boards ----------
export const lookupApplication = (email) =>
  api.get("/applications/lookup", { params: { email } }).then((r) => r.data);

export const submitMoodBoard = (applicationId, payload) =>
  api.post(`/applications/${applicationId}/moodboard`, payload).then((r) => r.data);

export const setMoodBoardStatus = (applicationId, moodboardId, status) =>
  api.patch(`/applications/${applicationId}/moodboard/${moodboardId}`, { status }).then((r) => r.data);

// ---------- Admin CRM: notes + meetings ----------
export const addNote = (applicationId, text) =>
  api.post(`/applications/${applicationId}/notes`, { text }).then((r) => r.data);

export const deleteNote = (applicationId, noteId) =>
  api.delete(`/applications/${applicationId}/notes/${noteId}`).then((r) => r.data);

export const addMeeting = (applicationId, payload) =>
  api.post(`/applications/${applicationId}/meetings`, payload).then((r) => r.data);

export const deleteMeeting = (applicationId, meetingId) =>
  api.delete(`/applications/${applicationId}/meetings/${meetingId}`).then((r) => r.data);


// Videos stay binary: small chunks bypass request limits; only a reference enters MongoDB.
export const uploadVerificationVideo = async (blob, onProgress) => {
  onProgress({ percent: 0, label: "Preparing private upload…" });
  const { data: upload } = await api.post("/verification/uploads", { size: blob.size, content_type: blob.type });
  const headers = { "X-Upload-Token": upload.upload_token, "Content-Type": "application/octet-stream" };
  for (let offset = 0, index = 0; offset < blob.size; offset += upload.chunk_size, index += 1) {
    const chunk = blob.slice(offset, offset + upload.chunk_size);
    await api.put(`/verification/uploads/${upload.id}/chunks/${index}`, chunk, {
      headers,
      onUploadProgress: ({ loaded }) => onProgress({
        percent: Math.round(90 * (offset + loaded) / blob.size), label: "Uploading your recording…",
      }),
    });
  }
  onProgress({ percent: 95, label: "Preparing video for the scouting team…" });
  await api.post(`/verification/uploads/${upload.id}/complete`, null, { headers, timeout: 120000 });
  onProgress({ percent: 100, label: "Video uploaded. Saving your application…" });
  return { verification_video_id: upload.id, verification_upload_token: upload.upload_token };
};

export const getVerificationVideo = (id) =>
  api.get(`/applications/${id}/verification-video`, { responseType: "blob", timeout: 120000 }).then((r) => r.data);
